import React, { useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { Helmet } from 'react-helmet';
import { DatoCmsGlobalSiteSetting } from '../../schema/datocms';
import { GatsbySite } from '../../schema/gatsby';
import Footer from '../footer/footer';
import Header from '../header/header';
import BannerAlert from '../banner-alert/banner-alert';
import { DatoCmsBannerAlert } from '../../schema/datocms/blocks/banner-alert';
import { AppConfig } from '../../environment/app-config.model';
import { HeaderType, HeaderTypes } from '../../schema/enum/header-types';
import useSuperscriptTags from '../../hooks/useSuperscriptTags';
import { setSessionCookie } from '../../utilities';
import { CanonicalUrl } from '../../schema/gatsby/canonical-url';
import { ComponentTypeNames } from '../../schema';

const isBrowser = typeof window !== 'undefined';

const LayoutStaticQuery = graphql`
    query LayoutQuery {
        site {
            siteMetadata {
                siteUrl
            }
        }
        environment: environmentJson {
            recaptchaSiteKey
        }
        global: datoCmsGlobalSiteSetting {
            siteTitle
            defaultPageTitleSuffix
            defaultPageDescription
            quoteButtonText
            quoteButtonStyle
            defaultPageShareImage {
                fixed(width: 1200, imgixParams: { fm: "png" }) {
                    src
                }
            }
            twitterAccount
            favicon {
                small: fixed(width:16, height:16, imgixParams:{ fm: "png" }) {
                    src
                }
                large: fixed(width:32, height:32, imgixParams:{ fm: "png" }) {
                    src
                }
            }
            bannerAlert {
                ...BannerAlertFragment
            }
        }
    }
`;

const Layout = ({ seo, pathname, options, children }: React.PropsWithChildren<LayoutProps>): React.ReactElement => {
    const { site, global, environment }: LayoutStaticQueryResult = useStaticQuery(LayoutStaticQuery);

    const pageTitle = `${ seo.pageTitle } ${ seo.titleSuffix?.trim() || global.defaultPageTitleSuffix?.trim() }`;

    const slug = pathname.startsWith('/') ? pathname : `/${pathname}`;
    const pageUrl = `${site.siteMetadata.siteUrl}${slug}`;

    const shareTitle = seo.shareTitle || pageTitle;

    const pageDescription = seo.description || global.defaultPageDescription;

    const imageUrl = seo.imageUrl || global.defaultPageShareImage?.fixed.src;
    const windowSearch: string = isBrowser ? window.location.search : '';
    const urlParams: URLSearchParams = new URLSearchParams(windowSearch);
    if(urlParams.toString()) {
        window.sessionStorage.setItem('urlSearch', urlParams.toString());
        setSessionCookie('urlSearch', urlParams.toString());
    }
    // page banner alert takes precedence over global banner
    let bannerAlert = null;
    if (options?.pageBannerAlert) {
        bannerAlert = options.pageBannerAlert;
    } else if (global.bannerAlert?.length === 1 && options?.hideGlobalBanner !== true) {
        bannerAlert = global.bannerAlert[0];
    }

    let canonicalUrl = pageUrl;
    if (options?.canonicalUrl && options?.canonicalUrl?.slug) {
        const canonicalUrlSlug = options.canonicalUrl.slug.startsWith('/') ? options.canonicalUrl.slug : `/${options.canonicalUrl.slug}`;
        if (options.canonicalUrl.__typename === ComponentTypeNames.BLOG_POST) {
            canonicalUrl = `${site.siteMetadata.siteUrl}/blog${canonicalUrlSlug}`;
        } else {
            canonicalUrl = `${site.siteMetadata.siteUrl}${canonicalUrlSlug}`;
        }
    }

    if (!canonicalUrl.endsWith('/')) {
        canonicalUrl = `${canonicalUrl}/`;
    }

    let bannerElement: JSX.Element | undefined = undefined;
    if (bannerAlert) {
        bannerElement = <BannerAlert bannerAlert={bannerAlert} key={2} />;
    }

    const [currentBannerAlert] = useState<JSX.Element | undefined>(bannerElement);

    // update the way registered symbols are displayed
    useSuperscriptTags('®', 'main-content');

    return (
        <React.Fragment>
            <Helmet title={shareTitle}>
                { global.favicon?.small && <link rel="icon" type="image/png" sizes="16x16" href={global.favicon.small.src} /> }
                { global.favicon?.large && <link rel="icon" type="image/png" sizes="32x32" href={global.favicon.large.src} /> }

                <meta property="og:site_name" content={global.siteTitle} />

                <link rel="canonical" href={canonicalUrl} />

                <meta property="og:url" content={pageUrl} />

                <meta property="og:type" content="website" />

                <meta property="og:title" content={shareTitle} />
                <meta name="twitter:title" content={shareTitle} />

                <meta name="description" content={pageDescription} />
                <meta property="og:description" content={pageDescription} />
                <meta name="twitter:description" content={pageDescription} />

                <meta name="twitter:image" content={imageUrl} />
                <meta property="og:image" content={imageUrl} />

                <meta property="twitter:site" content={`@${global.twitterAccount}`} />
                <meta name="twitter:card" content={seo.twitterCardSize} />

                <meta property="og:locale" content="en_EN" />
                <link rel="stylesheet" href="https://maxcdn.bootstrapcdn.com/font-awesome/4.7.0/css/font-awesome.min.css" />

                <script src={`https://www.google.com/recaptcha/api.js?render=${environment.recaptchaSiteKey}`}></script>

                { seo.schemasJsonString && <script type="application/ld+json">{seo.schemasJsonString}</script> }
            </Helmet>

            {
                // if page doens't have a header, but has a banner, it still needs to be rendered
                options?.headerType !== HeaderTypes.NONE ?
                    <Header
                        simple={ options?.headerType === HeaderTypes.SIMPLE }
                        getQuoteOptions={ { showOnDesktop: options?.showGetQuoteInHeader, text: global.quoteButtonText, style: global.quoteButtonStyle } }
                        banner={currentBannerAlert} /> :
                    currentBannerAlert
            }

            <main id="main-content" className="min-h-screen" role="main">
                {children}
            </main>

            { options?.hideFooter === true ? null : <Footer /> }
        </React.Fragment>
    );
};

export default Layout;

export interface SeoMetadata {
    pageTitle: string;
    titleSuffix?: string;
    shareTitle?: string;
    description?: string;
    twitterCardSize?: string;
    imageUrl?: string;
    schemasJsonString?: string;
}

export interface LayoutProps {
    seo: SeoMetadata;
    pathname: string;
    options?: {
        headerType?: HeaderType,
        hideFooter?: boolean,
        pageBannerAlert?: DatoCmsBannerAlert,
        showGetQuoteInHeader?: boolean,
        hideGlobalBanner?: boolean,
        canonicalUrl?: CanonicalUrl
    };
}

interface LayoutStaticQueryResult {
    global: Partial<DatoCmsGlobalSiteSetting>;
    site: GatsbySite;
    environment: Partial<AppConfig>;
}
